import { DatePipe } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { FormatDownloadOption } from '@payments/models/invoice-downloads';
import { PaymentViewModel } from '@payments/models/payment-activity-page-view-model';
import { InvoiceDownloadModalComponent } from '@payments/payments-shared/invoice-download-modal/invoice-download-modal.component';
import { isValidTransactionNumber } from '@payments/selectors/payments.selector';
import * as FILES from '@shared/constants/files';
import { CONTENT_TYPE_PDF } from '@shared/constants/files';
import { SentenceCasePipe } from '@shared/pipes/sentence-case.pipe';
import { DownloadPaymentActivityService } from '@shared/services/document/download-payment-activity/download-payment-activity.service';
import {
  FileHandlerBaseService,
  OpenFileError,
  PrintFileError,
  SaveFileError,
  ShareFileError,
} from '@shared/services/file-handler/file-handler-base.service';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import {
  DeviceNotAvailableError,
  VersionError,
} from '@shared/services/sharesheet/sharesheet.service';
import { ToastService } from '@shared/services/toast/toast.service';
import {
  DATE_FORMAT,
  PaymentsPDFGeneration,
  TransformData,
} from '@usf/file-share-utils';
import { AlertAnalyticsService } from '@usf/ngrx-alerts';
import {
  ARDocFormat,
  CustomerReview,
  DownloadARStatementsResponse,
  IsrDocFormat,
  ISRDocumentResponse,
  PaymentActivity,
  PaymentDetail,
  PaymentSummary,
  PaymentTransactionsViewModel,
} from '@usf/payment-types';
import jsPDF from 'jspdf';
import { PaymentsPaymentService } from './payments-payment.service';

export class FileGenError extends Error {}

export interface PaymentActivityInvoice {
  customerName: string;
  customerNumber: number;
  documentDate?: string;
  primaryTransactionNumber: string;
  primaryTransactionType: string;
  secondaryTransactionNumber: string;
  secondaryTransactionType: string;
  referenceNumber: string;
  paymentMethod: string;
  paidAmount: number;
  paymentDate: string;
  access: boolean;
}

export interface AllInvoices {
  customerName: string;
  customerNumber: number;
  primaryTransactionNumber: string;
  primaryTransactionType: string;
  secondaryTransactionNumber: string;
  secondaryTransactionType: string;
  customerPO: string;
  orderNumber?: number;
  dateIssued: string;
  amount: number;
  amountDue: number;
  dueDate: string;
  status: string;
}

@Injectable({ providedIn: 'root' })
export class FileHandlerPaymentsService extends FileHandlerBaseService {
  emptyCell = '';
  timezone = 'GMT';
  constructor(
    toastService: ToastService,
    translateService: TranslateService,
    platform: Platform,
    datePipe: DatePipe,
    sentenceCasePipe: SentenceCasePipe,
    loadingSpinnerService: LoadingSpinnerService,
    private paymentsPaymentService: PaymentsPaymentService,
    private downloadPaymentActivityService: DownloadPaymentActivityService,
    private modalService: ModalService,
    private modalController: ModalController,
    private alertAnalyticsService: AlertAnalyticsService,
  ) {
    super(
      toastService,
      translateService,
      platform,
      datePipe,
      sentenceCasePipe,
      loadingSpinnerService,
    );
  }

  async sharePaymentConfirmation({
    paymentSummary,
    customers,
  }: {
    paymentSummary: PaymentSummary;
    customers: CustomerReview[];
  }) {
    try {
      const fileName = this.composeFileName('Payment Confirmation');
      const fileBlob = PaymentsPDFGeneration.formatPaymentConfirmationToPDFBlob(
        paymentSummary,
        customers,
        fileName,
      ) as Blob;

      // alerts analytics
      this.alertAnalyticsService.trackShareFilePaymentConfirmation();

      const shareOptions = { title: fileName };
      await this.shareFileNative(fileBlob, fileName, '.pdf', shareOptions)
        .then()
        .catch(e => {
          throw e;
        });
    } catch (e) {
      this.handleErrorMessage(e, 'paymentConfirmation.fileShareError');
    }
  }

  downloadPaymentActivity(payments: PaymentViewModel[]) {
    const fileName = this.composeFileName('Payment Activity');
    const rows = payments
      .map(vm => {
        return [...vm.paymentDetails]
          .sort(this.sortByCustomerAccessAndName)
          .map(detail => {
            const invoices = detail.invoices?.map(invoice => {
              const documentDate =
                this.datePipe.transform(
                  TransformData.convertDate(invoice.issueDate),
                  DATE_FORMAT,
                  'UTC',
                ) || null;
              const paymentDate = this.changeToUTC(vm.paymentDate);
              const hasSecondaryTransaction =
                isValidTransactionNumber(invoice.secondaryTransaction) &&
                isValidTransactionNumber(invoice.originalTransaction);
              const secondaryTransactionType = hasSecondaryTransaction
                ? invoice.transactionDescription
                : this.emptyCell;
              const secondaryTransactionNumber = hasSecondaryTransaction
                ? invoice.secondaryTransaction
                : this.emptyCell;
              const primaryTransactionType =
                invoice.isr && !hasSecondaryTransaction
                  ? invoice.transactionDescription
                  : (!invoice.isr || hasSecondaryTransaction) &&
                      invoice.originalTransaction !== '0'
                    ? 'Invoice'
                    : this.emptyCell;
              const primaryTransactionNumber =
                hasSecondaryTransaction ||
                isValidTransactionNumber(invoice.originalTransaction)
                  ? invoice.originalTransaction
                  : invoice.secondaryTransaction;

              const invoiceRow: PaymentActivityInvoice = {
                customerName: detail.customerName,
                customerNumber: detail.customerNumber,
                primaryTransactionNumber,
                primaryTransactionType,
                secondaryTransactionNumber,
                secondaryTransactionType,
                referenceNumber: vm.referenceNumber,
                paymentMethod: vm.paymentMethod,
                paidAmount: invoice.payAmount,
                paymentDate,
                access: detail.userHasAccessToCust,
              };
              if (!!documentDate) {
                invoiceRow.documentDate = documentDate;
              }
              return invoiceRow;
            });
            if (!invoices || invoices.length === 0) {
              const paymentDate = this.changeToUTC(vm.paymentDate);
              return [
                {
                  customerName: detail.customerName,
                  customerNumber: detail.customerNumber,
                  primaryTransactionNumber: this.emptyCell,
                  primaryTransactionType: this.emptyCell,
                  secondaryTransactionNumber: this.emptyCell,
                  secondaryTransactionType: this.emptyCell,
                  referenceNumber: vm.referenceNumber,
                  paymentMethod: vm.paymentMethod,
                  paidAmount: detail.paymentAmount,
                  paymentDate,
                  access: detail.userHasAccessToCust,
                } as PaymentActivityInvoice,
              ];
            }
            return invoices;
          })
          .reduce((previous, current) => {
            return [...previous, ...current];
          });
      })
      .reduce((previous, current) => {
        return [...previous, ...current];
      });

    this.downloadPaymentActivityService.generateAndSaveExcel(fileName, rows);
  }

  async shareInvoices(
    fileName: string,
    transactions: PaymentTransactionsViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    let dateLabel = this.translateService.instant('i18n.common.noneSelected');
    if (fromDate && toDate) {
      dateLabel =
        this.datePipe.transform(fromDate, DATE_FORMAT, this.timezone) +
        ' - ' +
        this.datePipe.transform(toDate, DATE_FORMAT, this.timezone);
    }
    try {
      const composedFileName = this.composeFileName(fileName);
      const fileBlob = PaymentsPDFGeneration.formatAllInvoicesToPDFBlob(
        transactions,
        dateLabel,
        composedFileName,
        fileName.toLocaleUpperCase(),
      ) as Blob;

      const shareOptions = { title: fileName };
      await this.shareFileNative(fileBlob, fileName, '.pdf', shareOptions)
        .then()
        .catch(e => {
          throw e;
        });
    } catch (e) {
      this.handleErrorMessage(e, 'fileShareError');
    }
  }

  async sharePaymentActivity(
    payments: PaymentViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    let dateLabel = this.translateService.instant('i18n.common.noneSelected');
    if (fromDate && toDate) {
      dateLabel =
        this.datePipe.transform(fromDate, DATE_FORMAT, this.timezone) +
        ' - ' +
        this.datePipe.transform(toDate, DATE_FORMAT, this.timezone);
    }
    try {
      const fileName = this.composeFileName('Payment Activity');
      const fileBlob = PaymentsPDFGeneration.formatPaymentActivityToPDFBlob(
        payments as unknown as PaymentActivity[],
        dateLabel,
        fileName,
      ) as Blob;

      const shareOptions = { title: fileName };
      await this.shareFileNative(fileBlob, fileName, '.pdf', shareOptions)
        .then()
        .catch(e => {
          throw e;
        });
    } catch (e) {
      this.handleErrorMessage(e, 'paymentActivity.fileShareError');
    }
  }

  printPaymentActivity(
    payments: PaymentViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    let dateLabel = this.translateService.instant('i18n.common.noneSelected');
    if (fromDate && toDate) {
      dateLabel =
        this.datePipe.transform(fromDate, DATE_FORMAT, this.timezone) +
        ' - ' +
        this.datePipe.transform(toDate, DATE_FORMAT, this.timezone);
    }
    try {
      const fileName = this.composeFileName('Payment Activity');
      const jsPDFdoc = PaymentsPDFGeneration.formatPaymentActivityToPDFBlob(
        payments.map(p => ({
          ...p,
          paymentDate: p.paymentDate.getTime(),
        })),
        dateLabel,
        fileName,
        'JSPDF_DOC',
      ) as jsPDF;

      jsPDFdoc.autoPrint();

      this.printWeb(jsPDFdoc.output('blob'));
    } catch (e) {
      this.handleErrorMessage(e, 'paymentActivity.filePrintError');
    }
  }

  printPaymentDetails(paymentActivity: PaymentViewModel) {
    try {
      const fileName = this.composeFileName('Payment Details');
      const jsPDFdoc = PaymentsPDFGeneration.formatPaymentDetailsToPDFBlob(
        {
          ...paymentActivity,
          paymentDate: paymentActivity.paymentDate.getTime(),
        },
        fileName,
        'JSPDF_DOC',
        typeof paymentActivity.paymentDate === 'number'
          ? this.paymentsPaymentService.createPaymentPostingDate(
              Number(paymentActivity.paymentDate),
            )
          : paymentActivity.paymentDate
              .toISOString()
              .substring(0, 10)
              .replace(/-/g, ''),
      ) as jsPDF;

      // This will open a print dialog after the file is opened in a new tab
      jsPDFdoc.autoPrint();

      this.printWeb(jsPDFdoc.output('blob'));
    } catch (e) {
      this.handleErrorMessage(e, 'paymentActivity.filePrintError');
    }
  }

  printPaymentConfirmation({
    paymentSummary,
    customers,
  }: {
    paymentSummary: PaymentSummary;
    customers: CustomerReview[];
  }) {
    try {
      const fileName = this.composeFileName('Payment Confirmation');
      const jsPDFdoc = PaymentsPDFGeneration.formatPaymentConfirmationToPDFBlob(
        paymentSummary,
        customers,
        fileName,
        'JSPDF_DOC',
      ) as jsPDF;

      // This will open a print dialog after the file is opened in a new tab
      jsPDFdoc.autoPrint();

      this.printWeb(jsPDFdoc.output('blob'));
    } catch (e) {
      this.handleErrorMessage(e, 'paymentConfirmation.filePrintError');
    }
  }

  downloadPaymentConfirmation({
    paymentSummary,
    customers,
  }: {
    paymentSummary: PaymentSummary;
    customers: CustomerReview[];
  }) {
    try {
      const fileName = this.composeFileName('Payment Confirmation');
      const blobFile = PaymentsPDFGeneration.formatPaymentConfirmationToPDFBlob(
        paymentSummary,
        customers,
        fileName,
      ) as Blob;
      this.downloadFile(blobFile, fileName, CONTENT_TYPE_PDF, true, false);
    } catch (e) {
      this.handleErrorMessage(e, 'paymentConfirmation.fileDownloadError');
    }
  }

  composeFileName(title: string) {
    return `${title} ${this.datePipe.transform(
      Date.now(),
      'yyyyMMdd HHmmssSS',
    )}`;
  }

  handleErrorMessage(e: Error, messageKey: string) {
    let errorCode = '';
    if (e instanceof OpenFileError) {
      errorCode = 'UIGE-1000';
    } else if (e instanceof SaveFileError) {
      errorCode = 'UIGE-2000';
    } else if (e instanceof FileGenError) {
      errorCode = 'UIGE-3000';
    } else if (e instanceof PrintFileError) {
      errorCode = 'UIGE-4000';
    } else if (e instanceof ShareFileError) {
      errorCode = 'UIGE-5000';
    } else if (e instanceof VersionError) {
      errorCode = 'UIGE-5001';
    } else if (e instanceof DeviceNotAvailableError) {
      errorCode = 'UIGE-5002';
    }
    if (errorCode === '' && !!e.message) errorCode = e.message;
    this.showErrorToast(`i18n.payments.${messageKey}`, errorCode);
  }

  private sortByCustomerAccessAndName(a: PaymentDetail, b: PaymentDetail) {
    if (!a.userHasAccessToCust && b.userHasAccessToCust) {
      return -1;
    } else if (a.userHasAccessToCust && !b.userHasAccessToCust) {
      return 1;
    } else {
      return a.customerName.localeCompare(b.customerName);
    }
  }

  downloadAllInvoices(
    fileName: string,
    paymentTransactions: PaymentTransactionsViewModel[],
    showOrderNumber: boolean,
  ) {
    const composedFileName = this.composeFileName(fileName);
    const rows = paymentTransactions.map(transaction => {
      const primaryTransactionNumber = transaction.transactionNumber;
      const primaryTransactionType = transaction.transactionTypeString;
      const secondaryTransactionNumber = transaction.hasSecondaryTransaction
        ? transaction.secondaryTransactionNumber
        : this.emptyCell;
      const secondaryTransactionType = transaction.hasSecondaryTransaction
        ? transaction.transactionTypeStringShort
        : this.emptyCell;

      const invoiceRow: AllInvoices = {
        customerName: transaction.customerName,
        customerNumber: transaction.customerNumber,
        primaryTransactionNumber,
        primaryTransactionType,
        secondaryTransactionType,
        secondaryTransactionNumber,
        customerPO: transaction.customerPO,
        dateIssued: this.changeToUTC(transaction.issueDate),
        amount: transaction.amount,
        amountDue: transaction.amountDue,
        dueDate: this.changeToUTC(transaction.dueDate),
        status: transaction.status,
        ...(showOrderNumber ? { orderNumber: transaction.orderNumber } : {}),
      };
      return invoiceRow;
    });

    this.downloadPaymentActivityService.generateAndSaveExcelForAllInvoices(
      composedFileName,
      rows,
      showOrderNumber,
    );
  }

  changeToUTC(date: Date) {
    const dateTransform = new Date(
      this.datePipe.transform(date, DATE_FORMAT, this.timezone),
    );
    // Add 16 seconds for xlsx files
    dateTransform.setTime(dateTransform.getTime() + 16 * 1000);
    return this.datePipe.transform(dateTransform, 'MM/dd/yyyy') || '';
  }

  printAllInvoices(
    fileName: string,
    transactions: PaymentTransactionsViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    let dateLabel = this.translateService.instant('i18n.common.noneSelected');
    if (fromDate && toDate) {
      dateLabel =
        this.datePipe.transform(fromDate, DATE_FORMAT, this.timezone) +
        ' - ' +
        this.datePipe.transform(toDate, DATE_FORMAT, this.timezone);
    }
    try {
      const composedFileName = this.composeFileName(fileName);
      const jsPDFdoc = PaymentsPDFGeneration.formatAllInvoicesToPDFBlob(
        transactions,
        dateLabel,
        composedFileName,
        fileName.toLocaleUpperCase(),
        'JSPDF_DOC',
      ) as jsPDF;

      jsPDFdoc.autoPrint();

      this.printWeb(jsPDFdoc.output('blob'));
    } catch (e) {
      this.handleErrorMessage(e, 'allInvoices.filePrintError');
    }
  }

  handleFormat(docFormat: string, fileNameOpt?: string) {
    const fileName = fileNameOpt ?? 'InvoiceDetails';
    switch (docFormat) {
      case ARDocFormat.CSV:
      case IsrDocFormat.CSV_FULL:
      case IsrDocFormat.CSV_LINES:
        return {
          fileName: fileName + '.csv',
          contentType: FILES.CONTENT_TYPE_CSV,
        };
      case IsrDocFormat.EXCELTAB:
      case IsrDocFormat.EXCELFLAT:
        return {
          fileName: fileName + '.xls',
          contentType: FILES.CONTENT_TYPE_EXCEL,
        };
      case ARDocFormat.PDF:
      case IsrDocFormat.PDF:
        return {
          fileName: fileName + '.pdf',
          contentType: FILES.CONTENT_TYPE_PDF,
        };
      case IsrDocFormat.QB_BILL:
        return {
          fileName: fileName + '.iif',
          contentType: FILES.CONTENT_TYPE_QUICKBOOKS,
        };
      case IsrDocFormat.XML:
        return {
          fileName: fileName + '.xml',
          contentType: FILES.CONTENT_TYPE_XML,
        };
      case ARDocFormat.ZIP:
      case IsrDocFormat.ZIP:
        return {
          fileName: fileName + '.zip',
          contentType: FILES.CONTENT_TYPE_ZIP,
        };
    }
  }

  base64ToByteArrayToBlob(dataString: string, contentType: string) {
    const byteArray = this.convertBase64ToByteArray(dataString);
    return new Blob([byteArray], {
      type: contentType,
    });
  }

  async chooseFormatDownloadModal(
    downloadEvent$: EventEmitter<ARDocFormat | IsrDocFormat>,
    downloadOptions: FormatDownloadOption[],
    isTouch: boolean,
  ) {
    this.modalService.setModalOptions(
      isTouch,
      InvoiceDownloadModalComponent,
      {
        isTouch,
        downloadOptions,
        downloadFormat: downloadEvent$,
      },
      'modal-desktop-size-lg',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    return await modal.present();
  }

  downloadAction(
    downloadResponse: DownloadARStatementsResponse | ISRDocumentResponse,
    isView = false,
    errorMessage: string,
    fileNameOpt?: string,
  ) {
    if (downloadResponse?.docData) {
      const { contentType, fileName } = this.handleFormat(
        downloadResponse.docFormat,
        fileNameOpt,
      );
      try {
        const blob = this.base64ToByteArrayToBlob(
          downloadResponse.docData,
          contentType,
        );

        if (isView && !Capacitor.isNativePlatform()) {
          this.printWeb(blob);
        } else {
          this.downloadFile(blob, fileName, contentType, !isView, false);
        }
      } catch (e) {
        this.handleErrorMessage(e, errorMessage);
      }
    } else {
      this.handleErrorMessage(new Error('FPGN-1013'), errorMessage);
    }
  }
}
